import { shallowEqual } from "react-redux";
import { useEffect, useState, useMemo } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

// Components
import CustomIcon from "components/CustomIcon";
import { ErrorBoundary } from "react-error-boundary";
import GenericError from "components/Errors/GenericError";
import { Typography, Box, Grid, Button } from "@mui/material";
import NavButton from "components/HeaderNavigation/NavButton";
import { BriefFacets } from "pages/Brief/components/BriefFacets";
import { BriefProfile } from "pages/Brief/components/BriefProfile";
import { BriefPosition } from "pages/Brief/components/BriefPosition";
import { Header, HeaderInner, Body } from "components/Layout/LayoutContent";
import { CreateProjectSectionModal, useCreateProjectSectionModal } from "components/CreateProjectSectionModal";
import { getInsightFilters } from "pages/ProjectDashboard";

// Store
import { clearChart, fetchCharts, getCharts } from "store/slice/charts";
import { TBriefData } from "store/slice/Project/projectData";
import { toggleLoader } from "store/slice/UI";
import { getBriefById } from "store/slice/Project/briefs/briefsSlice";
import { getInsightById } from "store/slice/Project/insights/insightsSlice";
import { initialBriefData } from "store/slice/Project/projectData/ProjectSlice";

// Hooks
import { useUnmount } from "ahooks";
import useGraphsFetch from "utils/hooks/useGraphsFetch";
import { useAppDispatch, useAppSelector } from "app/hooks";

// Others
import icons from "enums/icons";
import { isEmpty } from "lodash";
import { buildParamsString, useRedirectOnProjectError } from "utils";
import { urlPaths } from "enums/urlPaths";

// Utils
import { convertBriefData } from "pages/Brief/Brief.functions";

// Types
import { ProjectSearchAICreationResponse } from "services/projects/project.types";

// Styles
import briefReviewStyles from "./BriefReview.styles";
import useBriefInitialFetch from "pages/Brief/useBriefInitialFetch";
import { addSearchAisData, getSearchAis } from "store/slice/Project/searchAis/searchAisSlice";
import { useIntl } from "react-intl";

const BriefReview = () => {
    const intl = useIntl();
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const classes = briefReviewStyles();
    const [searchParams] = useSearchParams();

    const { companyId: companyIdParams } = useParams();
    const projectId = searchParams.get("projectId");
    const insightId = searchParams.get("insightId");
    const briefId = searchParams.get("briefId");
    const searchAiId = searchParams.get("searchAiId");

    // Redirect to 404 if invalid project id or not enough permission
    useRedirectOnProjectError([projectId, insightId, briefId]);

    // Data
    const { data: chartData, status: chartStatus } = useAppSelector(getCharts);
    const { data: brief } = useAppSelector(getBriefById(Number(briefId)), shallowEqual);
    const { data: insightData } = useAppSelector(getInsightById(Number(insightId)), shallowEqual);
    const { data: searchAisList } = useAppSelector(getSearchAis);

    // Insight's Peers and SLT
    const companyId = companyIdParams || getInsightFilters(insightData)?.data.company.id || undefined;

    // Brief Data
    const convertedBrief = convertBriefData(brief) || initialBriefData;
    const [briefData, setBriefData] = useState<TBriefData>(convertedBrief);

    // Charts flags
    const graphData = useGraphsFetch(["LD4", "LD5"], false);
    const { target_company_board: selectedBoard } = graphData;
    const hasDataForRequest = Boolean(companyId && selectedBoard.length);

    const handleRedirectAfterSavingSearchAI = async (createdSectionData: ProjectSearchAICreationResponse) => {
        const searchAiId = createdSectionData?.data.id;

        if (searchAiId) {
            dispatch(toggleLoader(false));
            const queryParams = buildParamsString({ projectId, insightId, briefId, searchAiId });
            const urlParams = `${companyId}?${queryParams}`;

            navigate(`${urlPaths.Talent}/${urlParams}`, {
                state: {
                    from: `${urlPaths.BriefReview}/${urlParams}`,
                },
            });
        }
    };

    const {
        showModalFor,
        newSectioName,
        handleShowCreateProjectSectionModal,
        handleCreateProjectSection,
        handleUpdateSectionName,
        handleCloseProjectSectionCreateModal,
    } = useCreateProjectSectionModal(projectId, briefId, handleRedirectAfterSavingSearchAI);

    const isSearchAiNameInvalid = useMemo(
        () => searchAisList.some((searchAi) => searchAi.data.name === newSectioName),
        [searchAisList, newSectioName]
    );

    const handleEditBrief = () => {
        const queryParams = buildParamsString({ projectId, insightId, briefId, searchAiId });
        const urlParams = `${companyId}?${queryParams}`;

        navigate(`${urlPaths.Brief}/${urlParams}`);
    };

    const handleGoToDashboard = () => {
        navigate(`${urlPaths.ProjectDashboard}/${projectId}`);
    };

    useUnmount(() => {
        dispatch(clearChart());
    });

    // Initial fetch
    useBriefInitialFetch(setBriefData);

    // Fetch charts
    useEffect(() => {
        const fetchGraphs = async () => {
            if (projectId && insightId) {
                try {
                    dispatch(toggleLoader(true));
                    await dispatch(fetchCharts({ graphData, projectId, insightId }));
                } catch (error) {
                    console.error(error);
                } finally {
                    dispatch(toggleLoader(false));
                }
            }
        };

        if (hasDataForRequest && isEmpty(chartData) && chartStatus === "pristine") {
            fetchGraphs();
        }
    }, [chartData, chartStatus, dispatch, graphData, hasDataForRequest, insightId, projectId]);

    return (
        <ErrorBoundary FallbackComponent={GenericError}>
            <Header isSticky backgroundColor="primary">
                <HeaderInner>
                    <Typography className={classes.headerTitle}>Review Your Brief</Typography>
                    <Box>
                        <NavButton isBackButton onClick={handleEditBrief} translationId="button.editBrief" />
                        <NavButton onClick={handleGoToDashboard} translationId="button.goToDashBoard" />
                    </Box>
                </HeaderInner>
            </Header>
            <Body>
                <Grid container sx={{ p: 3 }}>
                    <Grid item xs={12} xl={9} mb={2}>
                        <Box sx={{ display: "flex", alignItems: "center", mr: 1, mb: 2 }}>
                            <Typography sx={{ flex: 1, fontSize: 16, fontWeight: 400, color: "secondary.main", mr: 1 }}>
                                {intl.formatMessage({ id: "reviewBrief.explanation" })}
                            </Typography>
                            <Button
                                variant="outlined"
                                startIcon={<CustomIcon icon={icons.flashLight} />}
                                onClick={() => handleShowCreateProjectSectionModal("SearchAi")}
                            >
                                Commence Search AI
                            </Button>
                        </Box>
                        {/* Position */}
                        <BriefPosition isReadOnly isEditable briefData={briefData} isLoading={chartStatus !== "done"} />
                        {/* Profile */}
                        <BriefProfile isReadOnly isEditable briefData={briefData} isLoading={chartStatus !== "done"} />
                    </Grid>

                    {/* Facets */}
                    <Grid item xs={12} xl={3}>
                        <BriefFacets
                            isReadOnly
                            isEditable
                            shouldStackFacets
                            briefData={briefData}
                            isLoading={chartStatus !== "done"}
                        />
                    </Grid>
                </Grid>
                <CreateProjectSectionModal
                    modalFor={showModalFor}
                    isNameInvalid={isSearchAiNameInvalid}
                    textInput={newSectioName}
                    textInputPlaceholder=""
                    handleTextInputChange={handleUpdateSectionName}
                    handleCreateSection={handleCreateProjectSection}
                    handleCloseModal={handleCloseProjectSectionCreateModal}
                />
            </Body>
        </ErrorBoundary>
    );
};

export default BriefReview;
